import { REDIRECT_PATHS, TopBarLayout } from "Components";
import { useCollectionData } from "react-firebase-hooks/firestore";
import styled, { css } from "styled-components";
import { useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  query,
  collection,
  where,
  getFirestore,
  Query,
} from "firebase/firestore";
import { useUser } from "auth";
import defaultImg from "Components/Images/default.jpg";
import { SearchSvg } from "Components/Images/SearchSvg";
import { AddSvg } from "Components/Images/AddSvg";
import { useMobileMediaQuery } from "hooks/useMobileMediaQuery";
import { TextInput } from "melodies-source/TextInput";
import { Button as PrimaryButton } from "melodies-source/Button";
import { ArtistPendingCard } from "./ArtistPendingCard";
import { ArtistSignup } from "models/artistSignup";
import { H1, H3, H4, Body1 } from "melodies-source/Text";
import { ReactComponent as ClockIcon } from "assets/svg/clock.svg";
import { useDesktopMediaQuery } from "hooks";
import NoArtist from "svg/NoArtist";
import { useCustomAppContext } from "contexts/CustomAppContext";
import { FlexColumn } from "Components/Flex";
import { getSignupUrl } from "Utils/getSignupUrl";
import { useTypesenseDocuments } from "hooks/useTypesenseDocument";
import { useQuery } from "react-query";
import { getArtistImageById } from "Utils/getArtistImageById";
import { Typesense } from "@max/common/typesense";
import {
  Loader,
  CircularGradientLoader,
} from "Components/CircularGradientLoader";
import { ReactComponent as MicrophoneIcon } from "assets/svg/microphone.svg";

interface PendingArtistProps {
  image: string;
  name: string;
}

const PendingArtist = ({ image, name }: PendingArtistProps) => {
  const isDesktop = useDesktopMediaQuery();
  const Heading = isDesktop ? H3 : H4;

  return (
    <ArtistPendingContainer>
      <Avatar src={image || defaultImg}>
        <ArtistPendingOverlay>
          <ClockIcon />
          <Heading>Artist Pending Review</Heading>
        </ArtistPendingOverlay>
      </Avatar>
      <Name>{name}</Name>
    </ArtistPendingContainer>
  );
};

interface Props {
  allowedGroupIds?: string[];
}

const ARTISTS_PER_PAGE = 20;

export const ArtistSelect: React.FC<Props> = ({ allowedGroupIds }) => {
  const {
    user: { uid },
  } = useUser();
  const isMobile = useMobileMediaQuery();
  const history = useHistory();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const isFirstLoaded = useRef(false);

  let [pendingSignups, loadingPendingSignups] = useCollectionData(
    query(
      collection(getFirestore(), "artist_signups"),
      where("uid", "==", uid),
      where("status", "==", "submitted"),
      where("action", "==", "new"),
    ) as Query<ArtistSignup>,
  );

  let {
    documents: searchedArtists,
    found,
    loading: loadingArtistGroups,
    error,
  } = useTypesenseDocuments({
    collection: "artist_groups",
    filter_by: allowedGroupIds
      ? `id:=[${allowedGroupIds.join(",")}]`
      : undefined,
    per_page: search || page > 1 ? ARTISTS_PER_PAGE : ARTISTS_PER_PAGE - 1,
    page: search || page === 1 ? 1 : page,
    // we should not have any more missing values for `sortOrder` after we migrated
    // that property to all typesense documents. However, this will properly
    // order ones which do not have a `sortOrder` to the end.
    sort_by: "sortOrder(missing_values: last):asc",
    q: search || "*",
    query_by: "name",
  });

  searchedArtists = searchedArtists.map((doc) => ({
    sortOrder: Number.MAX_SAFE_INTEGER,
    ...doc,
  }));

  // as a user `views more` artists, they are added to this array to display
  // when a user is not searching
  const [displayedArtists, setDisplayedArtists] = useState<
    Typesense<"artist_groups">[]
  >([]);

  const artistsLoadedCount = ARTISTS_PER_PAGE * page - 1;
  useEffect(() => {
    if (displayedArtists.length >= artistsLoadedCount) return;
    // dedupe
    const newDisplayedArtists = [
      ...displayedArtists,
      ...searchedArtists,
    ].reduce((acc, artistGroup) => {
      // not present yet
      if (!acc.find(({ id }) => artistGroup.id === id)) {
        acc.push(artistGroup);
      }
      return acc;
    }, []);
    if (newDisplayedArtists.length > displayedArtists.length) {
      setDisplayedArtists(newDisplayedArtists);
    }
  }, [displayedArtists, searchedArtists, artistsLoadedCount]);

  const { customApp, language } = useCustomAppContext();

  const unloadedArtistsCount = found - displayedArtists.length;
  const noDisplayedArtists = !loadingArtistGroups && !displayedArtists.length;
  const noPendingSignups = !pendingSignups?.length && !loadingPendingSignups;

  // user is only member of one artist group, take directly to artist's dashboard
  if (displayedArtists.length === 1 && !search && noPendingSignups) {
    history.replace(`/${displayedArtists[0].id}/`);
  }

  if (REDIRECT_PATHS.includes(location.pathname)) {
    history.push(
      `/${displayedArtists[0].id}${location.pathname}${location.search}`,
    );
  }

  // if we have a search term, filter the pending signups by name
  // we already filter the rest of the artist groups in the typesense query
  if (search) {
    pendingSignups = pendingSignups?.filter((doc) =>
      doc.artistName?.toLowerCase().includes(search.toLowerCase()),
    );
  }

  useEffect(() => {
    if (!isFirstLoaded.current && !loadingArtistGroups) {
      isFirstLoaded.current = true;
    }
  }, [loadingArtistGroups]);

  const CounterText = isMobile ? H3 : H1;
  const MoreArtistsText = isMobile ? Body1 : H3;

  const content = (
    <Container isCustomApp={!!customApp}>
      {noDisplayedArtists && pendingSignups?.length === 1 && search === "" ? (
        <ArtistPendingCard
          image={pendingSignups[0].artistImage || defaultImg}
        />
      ) : (
        <>
          {!customApp && (
            <Controls>
              <ControlsWrap
                hasSearch={displayedArtists.length > 9 || !!search?.length}
              >
                <Header>Select Artist</Header>
                {displayedArtists.length > 0 && (
                  <StyledButton
                    variant="outlined"
                    leftIcon={<AddSvg />}
                    onClick={() => window.open(getSignupUrl(), "_self")}
                  >
                    Add an Artist
                  </StyledButton>
                )}
              </ControlsWrap>
              {(displayedArtists.length > 9 ||
                loadingArtistGroups ||
                search) && (
                <TextInput
                  placeholder={`Search for an Artist`}
                  value={search}
                  onChange={setSearch}
                  leftIcon={<SearchSvg />}
                  style={{
                    maxWidth: 600,
                    margin: isMobile ? "10px auto" : "40px auto 10px",
                  }}
                />
              )}
            </Controls>
          )}
          <ContainerPadding
            sm={displayedArtists.length < 4 || isMobile}
            isCustomApp={!!customApp}
          >
            {search && loadingArtistGroups && (
              <ArtistContainer isCustomApp={!!customApp} $loadMore>
                <SearchCircularLoader
                  color="var(--main-color)"
                  icon={
                    <MicrophoneIcon
                      style={{
                        color: "var(--main-color)",
                        width: 55,
                        height: 55,
                      }}
                    />
                  }
                  text={
                    <H4 style={{ color: "var(--main-color)" }}>
                      Finding Artists...
                    </H4>
                  }
                />
              </ArtistContainer>
            )}
            {(search
              ? loadingArtistGroups
                ? []
                : searchedArtists
              : displayedArtists
            ).map((doc) => (
              <Artist key={doc.id} artist={doc} isCustomApp={!!customApp} />
            ))}
            {!customApp && unloadedArtistsCount > 0 && !search && (
              <ArtistContainer isCustomApp={!!customApp} $loadMore>
                <Avatar artistCounter={!loadingArtistGroups}>
                  <FlexColumn
                    xCenter
                    yCenter
                    style={{
                      width: loadingArtistGroups ? "100%" : "90%",
                      height: "100%",
                    }}
                  >
                    {loadingArtistGroups ? (
                      <LoadingArtistsContainer>
                        <CircularLoader color="var(--main-color)" />
                        <LoadingArtistsText>
                          Loading Artists...
                        </LoadingArtistsText>
                      </LoadingArtistsContainer>
                    ) : (
                      <>
                        <CounterText
                          style={{ lineHeight: isMobile ? "20px" : "30px" }}
                        >
                          {unloadedArtistsCount}
                        </CounterText>
                        <MoreArtistsText>
                          more {language.event.owner.plural.toLowerCase()}
                        </MoreArtistsText>
                        <ViewMoreLinkText
                          onClick={() =>
                            !loadingArtistGroups && setPage((page) => page + 1)
                          }
                        >
                          View More
                        </ViewMoreLinkText>
                      </>
                    )}
                  </FlexColumn>
                </Avatar>
              </ArtistContainer>
            )}
            {!customApp &&
              isFirstLoaded.current &&
              pendingSignups?.map(({ artistImage, artistName }) => (
                <PendingArtist image={artistImage} name={artistName} />
              ))}
          </ContainerPadding>
          {/** NOTE: we use `searchedArtists` and not `displayedArtists`.
           *  `displayedArtists` is the already loaded artist typesense pages
           * */}
          {search && !loadingArtistGroups && !searchedArtists?.length && (
            <NoResults>No results found</NoResults>
          )}
          {noDisplayedArtists && noPendingSignups && !search && (
            <NoArtistContainer>
              <NoArtist />
              {error ? (
                <H3 style={{ margin: "20px auto 30px", textAlign: "center" }}>
                  The system encountered an error - <br /> please try again.
                </H3>
              ) : (
                <>
                  <H3 style={{ margin: "20px auto 30px", textAlign: "center" }}>
                    It looks like you don’t have any <br />{" "}
                    {customApp
                      ? customApp.event.owner.plural.toLowerCase()
                      : "artists"}{" "}
                    on your account yet.
                  </H3>
                  {!customApp && (
                    <PrimaryButton
                      style={{ width: "fit-content" }}
                      onClick={() => window.open(getSignupUrl(), "_self")}
                    >
                      <AddSvg />
                      Add an Artist
                    </PrimaryButton>
                  )}
                </>
              )}
            </NoArtistContainer>
          )}
        </>
      )}
    </Container>
  );
  return customApp ? content : <TopBarLayout>{content}</TopBarLayout>;
};

const LoadingArtistsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
`;

const CircularLoader = styled(Loader)`
  width: 85%;
  height: 85%;
`;

const LoadingArtistsText = styled(H3)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: var(--text-color);

  ${({ theme }) => theme.media.mobile} {
    font-size: 16px;
    line-height: 1.4;
  }
`;

const SearchCircularLoader = styled(CircularGradientLoader)`
  position: absolute;
  top: 150px;
  left: 50%;
  transform: translate(-50%, -50%);
`;

interface CustomAppProp {
  isCustomApp: boolean;
}

const Container = styled.div<CustomAppProp>`
  padding: ${({ isCustomApp }) => (isCustomApp ? 0 : "12px 18px 24px")};
  width: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;

interface ContainerPaddingProps extends CustomAppProp {
  sm?: boolean;
  md?: boolean;
}

const ContainerPadding = styled.div<ContainerPaddingProps>`
  width: 100%;
  max-width: 1550px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 20px;
  z-index: 5;
  position: relative;

  ${({ sm }) =>
    sm &&
    css`
      grid-template-columns: 1fr 1fr;
      max-width: 850px;
      gap: 5px;
    `}

  ${({ isCustomApp }) =>
    isCustomApp &&
    css`
      row-gap: 30px;
      column-gap: 5vw;

      ${({ theme }) => theme.media.tablet} {
        row-gap: 25px;
        column-gap: 10w;
      }

      ${({ theme }) => theme.media.mobile} {
        row-gap: 20px;
        column-gap: 20w;
      }
    `}
`;

const NoArtistContainer = styled.div`
  display: flex;
  margin: 100px auto;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  z-index: 5;
  max-width: 400px;
`;
const Controls = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  z-index: 5;
  max-width: 1550px;
  padding: 20px 30px;

  ${({ theme }) => theme.media.mobile} {
    padding: 10px 20px;
  }
`;
const ControlsWrap = styled.div<{ hasSearch: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  z-index: 5;
  margin-top: 15px;
  ${({ theme }) => theme.mediaQueries.mobile} {
    margin-bottom: 30px;
  }
`;

const StyledButton = styled(PrimaryButton)`
  max-width: 200px;
  min-width: fit-content;
  @media (max-width: 768px) {
    border-color: transparent;
  }
`;

const Artist = ({ artist, isCustomApp }) => {
  const data = artist;

  const { data: profileImageUrl } = useQuery({
    enabled: true,
    queryKey: ["artist_profile_image", data.id],
    queryFn: () => getArtistImageById(data.id),
  });

  return (
    <ArtistContainer as={Link} to={`/${artist.id}/`} isCustomApp={isCustomApp}>
      <Avatar src={profileImageUrl || defaultImg} />
      <Name>{data?.name}</Name>
    </ArtistContainer>
  );
};

const Header = styled.div`
  color: #333333;
  font-family: Poppins;
  font-size: 36px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 39px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 27px;
  }
`;

const ArtistContainer = styled.div<CustomAppProp & { $loadMore?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-decoration: none;
  padding: 30px 10px 0;
  max-width: 340px;
  cursor: ${({ $loadMore }) => ($loadMore ? "cursor" : "pointer")};
  transition: all ease-in-out 100ms;

  &:hover {
    transform: scale(1.05);
  }

  ${({ isCustomApp }) =>
    isCustomApp &&
    css`
      padding: 0;
      max-width: unset;

      ${Avatar} {
        width: 100%;
      }
    `}
`;

const Avatar = styled.div<{ src?: string; artistCounter?: boolean }>`
  width: 85%;
  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 95%;
  }
  background-image: url(${({ src }) => src});
  background-size: cover;
  background-position: center center;
  border-radius: 300px;
  border: 2px solid white;
  box-shadow: 0 0px 17px 9px rgba(0, 0, 0, 0.16);
  aspect-ratio: 1/1 !important;
  z-index: 15;
  position: relative;
  overflow: hidden;

  img {
    position: absolute;
    z-index: 20;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    object-fit: cover;
  }

  ${({ artistCounter }) =>
    artistCounter &&
    `
      color: black;
      background-color: #dbe4e4d7;
      box-shadow: none;
      border: 2px solid #dbe4e4d7;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      font-size: clamp(18px, 2.5vw, 25px);
      line-height: 36px;
      font-weight: 500;
    `}
`;

const Name = styled.div`
  color: var(--text-color);
  font-family: Poppins;
  font-size: 20px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 28px;
  padding: 20px;
  text-align: center;
  @media (max-width: 1100px) {
    font-size: clamp(13px, 2vw, 20px);
    line-height: 150%;
  }
`;

const ArtistPendingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 10px 0;
  max-width: 340px;
`;

const ArtistPendingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  background: rgba(0, 0, 0, 0.5);
  height: 100%;
  width: 100%;
  color: white;

  ${H3}, ${H4} {
    color: white;
    text-align: center;
  }

  ${H3} {
    width: 145px;
  }

  ${H4} {
    width: 120px;
  }

  ${({ theme }) => theme.media.mobile} {
    svg {
      width: 35px;
      height: 35px;
    }
  }
`;

const NoResults = styled(Header)`
  color: #666666;
  margin-top: 40px;
  z-index: 1;
`;

const ViewMoreLinkText = styled.a`
  font-size: 16px;
  line-height: 150%;
  font-family: Poppins;
  text-decoration: underline;
  margin-top: 20px;
  cursor: pointer;
  font-weight: 500;
  color: var(--max-action);

  ${({ theme }) => theme.mediaQueries.mobile} {
    font-size: 14px;
    line-height: 18px;
  }
`;
